import { useEffect, useId, useState, type ReactNode } from 'react'
import { cn } from '@/lib/format'
import { SaveStatus } from './SaveStatus'
import { useSaveAction } from './useSaveAction'

export interface ToggleProps {
  checked: boolean
  onChange: (next: boolean) => void
  /** Accessible name. Use when there's no visible label (or pass labelledBy). */
  label?: string
  labelledBy?: string
  describedBy?: string
  disabled?: boolean
  /** Dims the switch and blocks input while a save is in flight. */
  pending?: boolean
  size?: 'sm' | 'md'
}

/** An on/off switch (role="switch"). On = solid ScoreLM orange. */
export function Toggle({ checked, onChange, label, labelledBy, describedBy, disabled, pending, size = 'md' }: ToggleProps) {
  const dims = size === 'sm' ? { track: 'h-[18px] w-8', knob: 'h-3 w-3', on: 'translate-x-[17px]' } : { track: 'h-[22px] w-[38px]', knob: 'h-4 w-4', on: 'translate-x-[16px]' }
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-busy={pending || undefined}
      // While a save is in flight the switch stays focusable (a disabled
      // button drops keyboard focus to <body>); it just ignores input.
      aria-disabled={pending || undefined}
      disabled={disabled}
      onClick={() => {
        if (!pending) onChange(!checked)
      }}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full p-[3px] transition-colors duration-150',
        'disabled:cursor-not-allowed aria-disabled:cursor-progress',
        disabled && 'opacity-50',
        pending && 'opacity-70',
        dims.track,
        checked ? 'bg-accent-solid' : 'bg-toggle-off',
      )}
    >
      <span
        aria-hidden
        className={cn('block rounded-full transition-transform duration-150', dims.knob, checked ? cn('bg-white', dims.on) : 'translate-x-0 bg-muted')}
      />
    </button>
  )
}

export interface ToggleRowProps {
  label: ReactNode
  description?: ReactNode
  checked: boolean
  /** Persist the change. The row shows Saving / Saved / error inline. */
  onToggle: (next: boolean) => Promise<unknown>
  disabled?: boolean
  /** Extra line under the description, e.g. a TODO or a dependency note. */
  note?: ReactNode
}

/**
 * A labelled switch that saves itself: optimistic flip, rollback on error,
 * outcome shown on the row. Used for add-ons and other per-control saves.
 */
export function ToggleRow({ label, description, checked, onToggle, disabled, note }: ToggleRowProps) {
  const id = useId()
  const save = useSaveAction()
  const [optimistic, setOptimistic] = useState<boolean | null>(null)
  const [lastAttempt, setLastAttempt] = useState<boolean>(!checked)
  const value = optimistic ?? checked

  // Optimistic: show the new position at once. On success it stays until the
  // parent's `checked` catches up (after its refetch); on error it falls
  // back to the stored value.
  useEffect(() => {
    setOptimistic(null)
  }, [checked])

  const flip = async (next: boolean) => {
    setLastAttempt(next)
    setOptimistic(next)
    const ok = await save.run(() => onToggle(next))
    if (!ok) setOptimistic(null)
  }

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 border-t border-line py-3 first:border-t-0 first:pt-0">
      <span id={`${id}-label`} className="font-semibold text-ink">
        {label}
      </span>
      <div className="row-span-2 flex items-center">
        <Toggle checked={value} onChange={flip} labelledBy={`${id}-label`} describedBy={description ? `${id}-desc` : undefined} disabled={disabled} pending={save.status === 'saving'} />
      </div>
      {description ? (
        <span id={`${id}-desc`} className="text-xs text-muted">
          {description}
        </span>
      ) : (
        <span />
      )}
      {note || save.status !== 'idle' ? (
        <div className="col-span-2 flex flex-wrap items-center gap-2 pt-1">
          {note}
          <SaveStatus state={save} onRetry={() => flip(lastAttempt)} />
        </div>
      ) : null}
    </div>
  )
}
